import React, { useContext, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import { getAvailableMonths } from '../../../../helpers/localization/localizationUtils';
import Select from '../../../elements/Select';
import Box from '../../../elements/Box';
import Text from '../../../elements/Text';
import Button from '../../../elements/Button';
import Icon from '../../../elements/Icon';
import { useEffect } from 'react';

function formatFunctions(months, direction, showMonthYear) {
  return months.map((item, index) => {
    // formatted to (MM YYYY)
    let name = `${item.name}`;
    if (showMonthYear) {
      name = `${item.name} ${item.year}`;
      if (direction === 'rtl') {
        name = `${item.year} ${item.name}`;
      }
    }
    return {
      name,
      value: index,
      key: name
    };
  });
}

const MonthNavigationDropdown = (props) => {
  const {
    months,
    onSelect,
    showShortMonths,
    navigationDropdownSelect,
    currentMonth: currentDate,
    previous,
    previousIcon,
    next,
    nextIcon,
    dateStyles,
    selectedStartDate,
    activeTab,
    enableDropDown,
    isMobile = false,
    showMonthYear = true
  } = props;
  const { direction, translate, localizationData, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  const selectRef = useRef(null);
  const previousRef = useRef(null);
  const nextRef = useRef(null);
  const [interactive, setInteractive] = useState(false);

  const options = getAvailableMonths(
    months,
    translate,
    localizationData,
    showShortMonths,
    dayjs(new Date())
  );

  const [index, setIndex] = useState(0);
  useEffect(() => {
    setIndex(options.findIndex((element) => element.month === dayjs(currentDate).get('month')));
  }, [currentDate, options]);

  const getMonthName = () => {
    return `${options[index].name}`;
  };
  const getMonthYear = () => {
    return `${options[index].year}`;
  };

  const onChange = (event) => {
    const month = options[event.target.value];
    setIndex(options.findIndex((element) => element.name === month.name));
    onSelect(month);
    setChangeCurrentMonth(!changeCurrentMonth);
  };

  const [renderA11yMessage, setRenderA11yMessage] = React.useState(undefined);
  const [changeCurrentMonth, setChangeCurrentMonth] = React.useState(null);

  React.useEffect(() => {
    if (changeCurrentMonth === null) {
      return;
    }
    setRenderA11yMessage(false);
    const showTimer = setTimeout(() => {
      setRenderA11yMessage(true);
      const hideTimer = setTimeout(() => {
        setRenderA11yMessage(false);
      }, 1000);
      return () => clearTimeout(hideTimer);
    }, 300);
    return () => clearTimeout(showTimer);
  }, [changeCurrentMonth]);

  const renderAccessibilityMessages = (
    <div className="fr-sr-only" aria-live="polite">
      {renderA11yMessage && (
        <>
          {`
  ${translate('cmp-low-fare-calendar-show-month', 'Current month')}
  ${options[index].name} ${options[index].year}`}
        </>
      )}
    </div>
  );
  const previousDisabled = () => {
    if (index === 0 || activeTab === 'end') {
      if (!(selectedStartDate && dayjs(selectedStartDate).isBefore(currentDate, 'day'))) {
        if (enableDropDown) {
          interactive && selectRef.current?.focus();
        } else {
          interactive && nextRef.current?.focus();
        }
        return true;
      }
    }
    return false;
  };
  const nextDisabled = () => {
    if (index === options.length - 1) {
      if (enableDropDown) {
        interactive && selectRef.current?.focus();
      } else {
        interactive && previousRef.current?.focus();
      }
      return true;
    }
    return false;
  };

  const styles = { ...style };
  styles.textAlignLast = 'center';
  return (
    <Box className={className} style={style}>
      {renderAccessibilityMessages}
      <Button
        focusReferences={previousRef}
        {...previous}
        ariaLabel={translate('cmp-low-fare-calendar-prev-month', 'Previous month')}
        disabled={previousDisabled()}
        onClick={() => {
          if (index >= 1) {
            setIndex(index - 1);
            const month = options[index - 1];
            onSelect(month);
            setInteractive(true);
            setChangeCurrentMonth(!changeCurrentMonth);
          }
        }}
        onBlur={() => setInteractive(false)}
        dataTestId="PrevBtn"
      >
        <Icon ariaHidden={true} icon="left-chevron" rotate {...previousIcon} />
      </Button>

      {!isMobile ? (
        <Select
          ref={selectRef}
          {...navigationDropdownSelect}
          showIconLeft={false}
          value={index}
          options={formatFunctions(options, direction, showMonthYear)}
          iconRight="keyboard_arrow_down"
          onChange={onChange}
          ariaLabel={translate('cmp-month-selector-label', 'Select the departure or return month')}
          disabled={!enableDropDown}
        />
      ) : (
        <Box {...dateStyles}>
          <Text type="span" marginWidth="2">
            {getMonthName()}
          </Text>
          {showMonthYear && (
            <Text type="span" marginWidth="2">
              {getMonthYear()}
            </Text>
          )}
        </Box>
      )}
      <Button
        focusReferences={nextRef}
        {...next}
        ariaLabel={translate('cmp-low-fare-calendar-next-month', 'Next month')}
        disabled={nextDisabled()}
        onClick={() => {
          if (index < options.length - 1) {
            setIndex(index + 1);
            const month = options[index + 1];
            onSelect(month);
            setInteractive(true);
            setChangeCurrentMonth(!changeCurrentMonth);
          }
        }}
        onBlur={() => setInteractive(false)}
        dataTestId="NextBtn"
      >
        <Icon ariaHidden={true} icon="right-chevron" rotate {...nextIcon} />
      </Button>
    </Box>
  );
};

MonthNavigationDropdown.propTypes = {
  /**
   * Months to show
   */
  months: PropTypes.number,
  /**
   * Function fire when change month
   */
  onSelect: PropTypes.func,
  /**
   * Function fire when month is lost focus
   */
  onBlur: PropTypes.func,
  /**
   * showShortMonths
   */
  showShortMonths: PropTypes.bool,
  /**
   * Props of navigationDropdownSelect
   */
  navigationDropdownSelect: PropTypes.objectOf(PropTypes.any),
  /**
   * Current selected month
   */
  currentMonth: PropTypes.instanceOf(Date)
};
MonthNavigationDropdown.defaultProps = {
  months: 3,
  onSelect: () => {},
  onBlur: () => {},
  showShortMonths: false,
  enableDropDown: true,
  navigationDropdownSelect: {},
  currentMonth: dayjs()
};
MonthNavigationDropdown.componentName = 'MonthNavigationDropdown';

export default MonthNavigationDropdown;
